<template>
  <div class="providers-list">
    <h1 class="listing-titles">
      {{ $t("PaymentsProvidersView-HowToPay") }}
    </h1>
    <template v-if="filteredProviders[0].length !== 0">
      <h3 v-if="displayNoCommissionBanner === true">
        {{ $t("Sin comisión") }}<span class="recommended">
          <img src="@/assets/icons/kid_star.svg">
          {{ $t("in-store-cash-Smallest-Recommended") }}
        </span>
      </h3>
      <ul>
        <li 
          v-for="provider in filteredProviders[0]" 
          :key="provider.name"
        >
          <PaymentProviderItem
            :payment-provider="provider"
            @select-payment-provider="handleSelectPaymentProvider"
          />
        </li>
      </ul>
    </template>
    <template v-if="filteredProviders[1].length !== 0">
      <h3>{{ $t("Con comisión") }}</h3>
      <ul>
        <li 
          v-for="provider in filteredProviders[1]" 
          :key="provider.name"
        >
          <PaymentProviderItem
            :payment-provider="provider"
            @select-payment-provider="handleSelectPaymentProvider"
          />
        </li>
      </ul>
    </template>
  </div>
  <BottomBar
    :text="$t('PaymentsProvidersView-PaymentAmount')"
    :amount="amount"
    :currency-symbol="currencySymbol"
    :term-label="$t(term)"
    @change-payment-amount="handleAmountChange"
  />
  <HighlightPopup 
    :is-visible="onHighlighted"
    @close-popup="onClosePopup('highLight')"
  >
    <h1>
      {{ $t("Popup-Pay-Without-Commissions") }}
    </h1>
    <h2>
      {{ $t("Popup-Save-Money-Using-These-Options") }}
    </h2>
    <ul class="savings-container">
      <li
        v-for="provider in highlightedProviders"
        :key="$t(provider.name)"
        class="savings"
      >
        <img 
          :src="provider.logo" 
          :alt="provider.displayName"
        >
      </li>
    </ul>
  </HighlightPopup>
  <template
    v-for="(group, key) in groupedProviders" 
    :key="key"
  >
    <HighlightPopup 
      v-if="group.length > 0" 
      :is-visible="showWarningPopUp" 
      @close-popup="onClosePopup('warning')"
    >
      <div>
        <div>
          <img 
            src="@/assets/icons/warn.svg" 
            alt="Warning"
          >
        </div>
        <h1>{{ group[0].title1 }}</h1>
        <h1>{{ group[0].title2 }}</h1>
        <p>{{ group[0].reason_text }}</p>
      </div>
    </HighlightPopup>
  </template>
</template>

<script lang="ts">
import {computed, defineComponent, Ref, UnwrapRef} from "vue";
import {usePaymentProviders} from "@/services/DataService";
import {useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import router from "@/router";
import {IPaymentProvider} from "@/types/IPaymentProvider";
import PaymentProviderItem from "@/components/PaymentProviderItem.vue";
import BottomBar from "@/components/BottomBar.vue";
import {AnalyticEventsNames,} from "@/helpers/google-analytics.helpers";
import HighlightPopup from "@/components/HighlightPopup.vue";
import {IInstallmentDetail} from "@/types/IInstallment";
import {getCookie, setCookie} from "@/helpers/cookieHelper";
import authHelper from "@/helpers/authHelper";
import {ActionLogTypeConst} from "@/types/IActionLogs";
import prefetchHelper from "@/helpers/prefetchHelper";
import {Countries} from "@/types/Countries";

export default defineComponent({
  name: "PaymentProvidersView",
  components: {HighlightPopup, BottomBar, PaymentProviderItem},
  props: {
    usingSP: {
      type: String,
      required: false,
      default: "false"
    }
  },
  emits: ["select-payment-provider", "change-payment-amount", "close-popup"],
  setup(props) {
    const {t} = useI18n();
    const router = useRouter();

    const store = useStore();
    const country = computed(() => store.state.payOptions.deviceInfo.country);
    const deviceTag = computed(
      () => store.state.payOptions.deviceInfo.deviceTag
    );
    const selectedInstallment = computed(
      () => store.state.payOptions.selectedInstallment as IInstallmentDetail
    );
    const amount = computed(() => selectedInstallment.value?.amount);
    const term = computed(() => selectedInstallment.value?.term);
    const currencySymbol = computed(
      () => store.state.payOptions.deviceInfo.currencySymbol
    );

    const paymentOption = computed(
      () => store.state.payOptions.deviceInfo.paymentOption
    );
    const providers: { paymentProviders: Ref<UnwrapRef<IPaymentProvider[]>> } =
      usePaymentProviders({
        country: country.value,
        paymentOption: paymentOption.value,
      });

    const filteredProviders = computed(() => {
      let resultProviders: IPaymentProvider[] = [];

      // First let's check if the country is set and remove inactive providers
      if (country.value) {
        resultProviders = providers.paymentProviders.value.filter(
          (provider) => provider.country === country.value && provider.active
        );
      } else {
        // no country set, return all providers except inactive ones
        resultProviders = providers.paymentProviders.value.filter(
          (provider) => provider.active
        );
      }
      resultProviders = resultProviders
        .filter((provider) => !provider.showGrouped)
        .sort((a, b) => a.order - b.order);

      let providersWithZeroFee = resultProviders.filter(
        (provider) => provider.fee === 0
      );
      const providersWithNonZeroFee = resultProviders.filter(
        (provider) => provider.fee !== 0
      );

      if (store.state.payOptions.superPacmanInstallment) {
        // FOR NOW WE REMOVE PAYJOY STORE AS OPTION IF CLIENT IS JOINTLOANLINE. THESE LINES SHOULD BE REMOVED ONCE IN-STORE PAYMENTS IS IMPLEMENTED FOR SUPER PACMAN. WE ALSO REMOVE STP.
        if (["JointLoanLine"].includes(store.state.payOptions.superPacmanInstallment.financeProduct.financeProductType)) {
          providersWithZeroFee = providersWithZeroFee.filter(
            (provider) => !["PAYJOY"].includes(provider.name)
          )
        }
      }

      return [providersWithZeroFee, providersWithNonZeroFee];
    });

    const highlightedProviders = computed(() => {
      return providers.paymentProviders.value.filter(
        (provider) => provider.highlight
      );
    });

    const groupedProviders = computed(() => {
      return providers.paymentProviders.value
        .filter((provider) => provider.active && !provider.enabled)
        .reduce((acc, provider) => {
          if (!acc[provider.name]) {
            acc[provider.name] = [];
          }
          acc[provider.name].push(provider);
          return acc;
        }, {} as Record<string, IPaymentProvider[]>);
    });

    const logoList = computed(() => {
      return Object.values(filteredProviders.value).flat().map((provider: IPaymentProvider) => provider.logo);
    });

    const displayNoCommissionBanner = country.value === Countries.MEXICO;

    return {
      t,
      providers,
      filteredProviders,
      highlightedProviders,
      router,
      country,
      deviceTag,
      amount,
      currencySymbol,
      selectedInstallment,
      paymentOption,
      groupedProviders,
      logoList,
      displayNoCommissionBanner,
      term,
      store
    };
  },
  data() {
    return {
      onHighlighted: true,
      showWarningPopUp: false,
    };
  },
  watch: {
    groupedProviders: {
      handler(groupedProviders) {
        if (Object.keys(groupedProviders).length > 0) {
          this.showWarningPopUp = true;
        }
      },
      immediate: true,
    },
    logoList: {
      handler(logoList) {
        if (logoList.length > 0)
          prefetchHelper.prefetchImages(logoList);
      },
      immediate: true,
    },
  },
  mounted() {
    // Check if the user has visited this page before
    const hasVisited = getCookie("hasVisitedPaymentProviders");
    if (hasVisited) {
      this.onHighlighted = false;
    } else {
      setCookie("hasVisitedPaymentProviders", "true", 365);
    }
    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page view
      authHelper.logAction({
        deviceTag: this.deviceTag,
        country: this.country,
        action: AnalyticEventsNames.OnSelectPaymentProviderPage,
        actionType: ActionLogTypeConst.PAGE_VIEW
      }, this.$gtag)
    }

    // Is on highlight PopUp? log the event
    if (this.onHighlighted) {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction({
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnSelectPaymentProviderPage_CommissionsModalOpened,
          actionType: ActionLogTypeConst.PAGE_VIEW
        }, this.$gtag)
      }
    }
  },
  beforeUnmount() {
    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page unmount
      authHelper.logAction({
        deviceTag: this.deviceTag,
        country: this.country,
        action: AnalyticEventsNames.OnSelectPaymentProviderPage_BrowserClose,
        actionType: ActionLogTypeConst.PAGE_VIEW
      }, this.$gtag)
    }

    this.router.beforeEach((to, from, next) => {
      if (to.name === "InstallmentsByDevice") {
        if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
          //Go back to InstallmentsByDevice, so log the action
          authHelper.logAction({
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_BrowserGoBack,
            actionType: ActionLogTypeConst.BUTTON_CLICK
          }, this.$gtag)
        }
      }
      next();
    });
  },
  methods: {
    handleSelectPaymentProvider(selectedPaymentProvider) {
      this.$emit("select-payment-provider", selectedPaymentProvider);

      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction({
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnSelectPaymentProviderPage_SelectPaymentProvider,
          actionType: ActionLogTypeConst.BUTTON_CLICK,
          data: {
            deviceTag: this.deviceTag,
            country: this.country,
            provider: selectedPaymentProvider,
          }
        }, this.$gtag);
      }

      router.push({
        name: "DynamicProviderLoader",
        params: {providerName: selectedPaymentProvider},
      });
    },
    handleAmountChange() {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction({
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnSelectPaymentProviderPage_ChangeInstallment,
          actionType: ActionLogTypeConst.BUTTON_CLICK,
        }, this.$gtag);
      }
      if (this.store.state.payOptions.superPacmanInstallment) {
        this.router.push(
          "/p/device/" + this.deviceTag + "?showoptions=" + this.paymentOption + "&forceUpdate=true"
        );
      } else {
        this.router.push(
          "/device/" + this.deviceTag + "?showoptions=" + this.paymentOption + "&forceUpdate=true"
        );
      }
    },
    onClosePopup(source: string) {
      this.$emit("close-popup");

      if (source === 'highLight') {
        this.onHighlighted = false;
        if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
          authHelper.logAction({
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_CloseCommissionsModal,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
          }, this.$gtag)
        }
      } else {
        this.showWarningPopUp = false;
      }
    },
  },
});
</script>

<style scoped>
.providers-list {
  padding: 16px 22px 350px 22px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.providers-list h1 {
  color: var(--Typography-Text-1);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 29px;
}

.providers-list h3 {
  color: var(--Typography-Text-2);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
}

.recommended {
  color: var(--Primary-Green-1);
  font-feature-settings: "clig" off, "liga" off;
  /* Text lighter */
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  background: var(--Alerts-Success-Success-Background);
  margin-left: 25px;
}

.recommended img {
  margin-bottom: -3px;
  margin-left: -18px;
}

.providers-list ul {
  list-style: none;
  padding: 0;
  margin-top: 0;
}

.providers-list ul > li {
  position: relative;
}

.message h1 {
  color: var(--Primary-Green-1);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 120% */
}

.message h2 {
  color: var(--Typography-Text-2);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
}

.savings-container {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  max-width: 230px;
  margin-left: auto;
  margin-right: auto;
  list-style: none;
  padding: 0;
}

.savings {
  background: #fff;
  display: flex;
}

.savings img {
  margin: auto;
  min-width: 50px;
  max-width: 70px;
}
</style>
